/**
 * ZoneLabelToggle — cycle the on-map zone area-code labels through three states:
 * full visibility → dimmed (80% transparent) → hidden, looping. Default is dimmed
 * so the codes stay legible without competing with the scene.
 */
import { Tag, TagsIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TOOLBAR_BASE, TOOLBAR_OFF, TOOLBAR_ON } from './toolbarStyles'

export type ZoneLabelMode = 'visible' | 'dimmed' | 'hidden'

/** Cycle order: visible → dimmed → hidden → (loop). */
export const ZONE_LABEL_MODES: ZoneLabelMode[] = ['visible', 'dimmed', 'hidden']

export function nextZoneLabelMode(mode: ZoneLabelMode): ZoneLabelMode {
  const i = ZONE_LABEL_MODES.indexOf(mode)
  return ZONE_LABEL_MODES[(i + 1) % ZONE_LABEL_MODES.length]
}

const TITLES: Record<ZoneLabelMode, string> = {
  visible: 'Zone labels: visible (click to dim)',
  dimmed: 'Zone labels: dimmed (click to hide)',
  hidden: 'Zone labels: hidden (click to show)',
}

interface Props {
  mode: ZoneLabelMode
  onCycle: () => void
  fullWidth?: boolean
}

export function ZoneLabelToggle({ mode, onCycle, fullWidth }: Props) {
  const Icon = mode === 'hidden' ? TagsIcon : Tag
  return (
    <button
      type="button"
      onClick={onCycle}
      title={TITLES[mode]}
      className={cn(
        TOOLBAR_BASE,
        fullWidth && 'w-full justify-start',
        mode === 'visible'
          ? TOOLBAR_ON.amber
          : mode === 'dimmed'
            ? 'border-amber-400/30 bg-amber-500/10 text-amber-200/70 hover:bg-amber-500/20'
            : TOOLBAR_OFF,
      )}
    >
      <Icon className="h-4 w-4" />
      Labels
      <span className="text-[10px] font-semibold tabular-nums opacity-70">
        {mode === 'visible' ? '100%' : mode === 'dimmed' ? '20%' : 'off'}
      </span>
    </button>
  )
}
